"use client";

import React, { useEffect, useState } from "react";
import {
  PreguntaPAES,
  SimulacionesService,
} from "../../../services/admin/simulaciones/simulaciones.service";
import { AdminLayout } from "../components";
import ImportarExcelModal from "../components/ImportarExcelModal";
import ImportarJSONModal from "../components/ImportarJSONModal";
import PreguntaDetailModal from "../components/PreguntaDetailModal";
import PreguntasPAESTable from "../components/PreguntasPAESTable";

const SimulacionesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState("preguntas-paes-m1");
  const [preguntas, setPreguntas] = useState<PreguntaPAES[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalPreguntas: 0,
    ultimoAnio: 2024,
    promedioDificultad: 3.0,
    tiempoPromedio: 60,
  });

  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedPregunta, setSelectedPregunta] = useState<PreguntaPAES | null>(
    null
  );
  const [modalMode, setModalMode] = useState<"view" | "edit" | "create">(
    "view"
  );

  // Estados para confirmación de eliminación
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [preguntaToDelete, setPreguntaToDelete] = useState<PreguntaPAES | null>(
    null
  );

  const tabs = [
    {
      id: "preguntas-paes-m1",
      name: "Preguntas PAES M1",
      description: "Preguntas de examen de Matemáticas PAES M1",
      icon: "📊",
    },
    {
      id: "preguntas-paes-m2",
      name: "Preguntas PAES M2",
      description: "Preguntas de examen de Matemáticas PAES M2",
      icon: "📈",
    },
    {
      id: "simulaciones",
      name: "Simulaciones",
      description: "Preguntas para simulaciones PAES",
      icon: "🎯",
    },
    {
      id: "resultados",
      name: "Resultados",
      description: "Ver resultados de simulaciones",
      icon: "📋",
    },
  ];

  // Cargar preguntas al montar el componente
  useEffect(() => {
    if (activeTab === "preguntas-paes-m1") {
      cargarPreguntas();
    } else if (activeTab === "preguntas-paes-m2") {
      cargarPreguntasM2();
    } else if (activeTab === "simulaciones") {
      cargarPreguntasSimulaciones();
    }
  }, [activeTab]);

  // Calcular total de páginas cuando cambien las preguntas
  useEffect(() => {
    const total = Math.ceil(preguntas.length / itemsPerPage);
    setTotalPages(total);
    // Si la página actual es mayor que el total de páginas, ir a la primera página
    if (currentPage > total && total > 0) {
      setCurrentPage(1);
    }
  }, [preguntas.length, itemsPerPage, currentPage]);

  const cargarPreguntas = async () => {
    setLoading(true);
    try {
      const response = await SimulacionesService.getPreguntasPAESM1();

      // Ordenar preguntas por número de menor a mayor
      const preguntasOrdenadas = response.preguntas.sort(
        (a: PreguntaPAES, b: PreguntaPAES) => a.numero - b.numero
      );
      setPreguntas(preguntasOrdenadas);
      setStats({
        totalPreguntas: response.pagination.total,
        ultimoAnio:
          response.preguntas.length > 0
            ? Math.max(...response.preguntas.map((p: PreguntaPAES) => p.anio))
            : 2024,
        promedioDificultad:
          response.preguntas.length > 0
            ? response.preguntas.reduce(
                (sum: number, p: PreguntaPAES) => sum + p.dificultad,
                0
              ) / response.preguntas.length
            : 3.0,
        tiempoPromedio:
          response.preguntas.length > 0
            ? Math.round(
                response.preguntas.reduce(
                  (sum: number, p: PreguntaPAES) => sum + p.tiempoEstimado,
                  0
                ) / response.preguntas.length
              )
            : 60,
      });
    } catch (error) {
      console.error("Error al cargar preguntas:", error);
    } finally {
      setLoading(false);
    }
  };

  const cargarPreguntasM2 = async () => {
    setLoading(true);
    try {
      const response = await SimulacionesService.getPreguntasPAESM2();

      // Ordenar preguntas por número de menor a mayor
      const preguntasOrdenadas = response.preguntas.sort(
        (a: PreguntaPAES, b: PreguntaPAES) => a.numero - b.numero
      );
      setPreguntas(preguntasOrdenadas);
      setStats({
        totalPreguntas: response.pagination.total,
        ultimoAnio:
          response.preguntas.length > 0
            ? Math.max(...response.preguntas.map((p: PreguntaPAES) => p.anio))
            : 2024,
        promedioDificultad:
          response.preguntas.length > 0
            ? response.preguntas.reduce(
                (sum: number, p: PreguntaPAES) => sum + p.dificultad,
                0
              ) / response.preguntas.length
            : 3.0,
        tiempoPromedio:
          response.preguntas.length > 0
            ? Math.round(
                response.preguntas.reduce(
                  (sum: number, p: PreguntaPAES) => sum + p.tiempoEstimado,
                  0
                ) / response.preguntas.length
              )
            : 60,
      });
    } catch (error) {
      console.error("Error al cargar preguntas M2:", error);
    } finally {
      setLoading(false);
    }
  };

  const cargarPreguntasSimulaciones = async () => {
    setLoading(true);
    try {
      const response = await SimulacionesService.getPreguntasSimulaciones();

      // Ordenar preguntas por número de menor a mayor
      const preguntasOrdenadas = response.preguntas.sort(
        (a: PreguntaPAES, b: PreguntaPAES) => a.numero - b.numero
      );
      setPreguntas(preguntasOrdenadas);
      setStats({
        totalPreguntas: response.pagination.total,
        ultimoAnio:
          response.preguntas.length > 0
            ? Math.max(...response.preguntas.map((p: PreguntaPAES) => p.anio))
            : 2024,
        promedioDificultad:
          response.preguntas.length > 0
            ? response.preguntas.reduce(
                (sum: number, p: PreguntaPAES) => sum + p.dificultad,
                0
              ) / response.preguntas.length
            : 3.0,
        tiempoPromedio:
          response.preguntas.length > 0
            ? Math.round(
                response.preguntas.reduce(
                  (sum: number, p: PreguntaPAES) => sum + p.tiempoEstimado,
                  0
                ) / response.preguntas.length
              )
            : 60,
      });
    } catch (error) {
      console.error("Error al cargar preguntas de simulaciones:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedPregunta(null);
    setModalMode("create");
    setShowDetailModal(true);
  };

  const handleEdit = (pregunta: PreguntaPAES) => {
    setSelectedPregunta(pregunta);
    setModalMode("edit");
    setShowDetailModal(true);
  };

  const handleDeleteClick = (pregunta: PreguntaPAES) => {
    setPreguntaToDelete(pregunta);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!preguntaToDelete) return;

    try {
      await SimulacionesService.eliminarPreguntaPAES(
        preguntaToDelete.idPreguntaPAES
      );
      // Recargar según el tab activo
      if (activeTab === "preguntas-paes-m1") {
        await cargarPreguntas();
      } else if (activeTab === "preguntas-paes-m2") {
        await cargarPreguntasM2();
      } else if (activeTab === "simulaciones") {
        await cargarPreguntasSimulaciones();
      }
      setShowDeleteConfirm(false);
      setPreguntaToDelete(null);
    } catch (error) {
      console.error("Error al eliminar pregunta:", error);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setPreguntaToDelete(null);
  };

  // Funciones para paginación
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return preguntas.slice(startIndex, endIndex);
  };

  const getPaginationInfo = () => {
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, preguntas.length);
    return { startItem, endItem, total: preguntas.length };
  };

  const handleView = (pregunta: PreguntaPAES) => {
    setSelectedPregunta(pregunta);
    setModalMode("view");
    setShowDetailModal(true);
  };

  const handleSavePregunta = async (
    preguntaData: PreguntaPAES & { imagen?: File }
  ) => {
    try {
      if (modalMode === "create") {
        // Crear nueva pregunta
        const { imagen, ...preguntaToCreate } = preguntaData;
        await SimulacionesService.crearPreguntaPAES({
          ...preguntaToCreate,
          imagen: imagen,
        });
      } else {
        // Actualizar pregunta existente
        const { imagen, ...preguntaToUpdate } = preguntaData;
        await SimulacionesService.actualizarPreguntaPAES(
          preguntaData.idPreguntaPAES,
          {
            ...preguntaToUpdate,
            imagen: imagen,
          }
        );
      }
      // Recargar según el tab activo
      if (activeTab === "preguntas-paes-m1") {
        await cargarPreguntas();
      } else if (activeTab === "preguntas-paes-m2") {
        await cargarPreguntasM2();
      } else if (activeTab === "simulaciones") {
        await cargarPreguntasSimulaciones();
      }
    } catch (error) {
      console.error("Error al guardar pregunta:", error);
      throw error;
    }
  };

  const handleImportSuccess = () => {
    if (activeTab === "preguntas-paes-m1") {
      cargarPreguntas();
    } else if (activeTab === "preguntas-paes-m2") {
      cargarPreguntasM2();
    } else if (activeTab === "simulaciones") {
      cargarPreguntasSimulaciones();
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "preguntas-paes-m1":
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Preguntas PAES M1 - Matemáticas
            </h2>
            <p className="text-gray-600 mb-6">
              Gestiona las preguntas de examen de Matemáticas para las pruebas
              PAES (solo estado EXAMEN y tipoSimulacion M1).
            </p>

            {/* Estadísticas rápidas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="text-blue-600 text-2xl mr-3">📚</div>
                  <div>
                    <p className="text-sm text-blue-600 font-medium">
                      Total Preguntas
                    </p>
                    <p className="text-2xl font-bold text-blue-800">
                      {stats.totalPreguntas}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="text-green-600 text-2xl mr-3">📅</div>
                  <div>
                    <p className="text-sm text-green-600 font-medium">
                      Último Año
                    </p>
                    <p className="text-2xl font-bold text-green-800">
                      {stats.ultimoAnio}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="text-purple-600 text-2xl mr-3">🎯</div>
                  <div>
                    <p className="text-sm text-purple-600 font-medium">
                      Promedio Dificultad
                    </p>
                    <p className="text-2xl font-bold text-purple-800">
                      {stats.promedioDificultad.toFixed(1)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="text-orange-600 text-2xl mr-3">⏱️</div>
                  <div>
                    <p className="text-sm text-orange-600 font-medium">
                      Tiempo Promedio
                    </p>
                    <p className="text-2xl font-bold text-orange-800">
                      {stats.tiempoPromedio}s
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Acciones rápidas */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <button
                onClick={handleCreate}
                className="bg-blue-600 text-white p-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">➕</span>
                  <div className="text-left">
                    <p className="font-semibold">Nueva Pregunta</p>
                    <p className="text-sm opacity-90">Crear pregunta PAES M1</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setShowImportModal(true)}
                className="bg-green-600 text-white p-4 rounded-lg hover:bg-green-700 transition-colors"
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">📤</span>
                  <div className="text-left">
                    <p className="font-semibold">Importar JSON</p>
                    <p className="text-sm opacity-90">
                      Pegar JSON de preguntas
                    </p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setShowExcelModal(true)}
                className="bg-orange-600 text-white p-4 rounded-lg hover:bg-orange-700 transition-colors"
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">📊</span>
                  <div className="text-left">
                    <p className="font-semibold">Importar Excel</p>
                    <p className="text-sm opacity-90">Cargar archivo Excel</p>
                  </div>
                </div>
              </button>

              <button className="bg-purple-600 text-white p-4 rounded-lg hover:bg-purple-700 transition-colors">
                <div className="flex items-center">
                  <span className="text-2xl mr-3">📊</span>
                  <div className="text-left">
                    <p className="font-semibold">Estadísticas</p>
                    <p className="text-sm opacity-90">Ver análisis detallado</p>
                  </div>
                </div>
              </button>
            </div>

            {/* Tabla de Preguntas */}
            <PreguntasPAESTable
              preguntas={getCurrentPageData()}
              loading={loading}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              onView={handleView}
            />

            {/* Paginación */}
            {preguntas.length > 0 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  {(() => {
                    const { startItem, endItem, total } = getPaginationInfo();
                    return `Mostrando ${startItem} a ${endItem} de ${total} preguntas`;
                  })()}
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>

                  <div className="flex space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === page
                              ? "bg-blue-600 text-white"
                              : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case "preguntas-paes-m2":
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Preguntas PAES M2 - Matemáticas
            </h2>
            <p className="text-gray-600 mb-6">
              Gestiona las preguntas de examen de Matemáticas M2 para las
              pruebas PAES (solo estado EXAMEN y tipoSimulacion M2).
            </p>

            {/* Estadísticas rápidas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="text-blue-600 text-2xl mr-3">📚</div>
                  <div>
                    <p className="text-sm text-blue-600 font-medium">
                      Total Preguntas
                    </p>
                    <p className="text-2xl font-bold text-blue-800">
                      {stats.totalPreguntas}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="text-green-600 text-2xl mr-3">📅</div>
                  <div>
                    <p className="text-sm text-green-600 font-medium">
                      Último Año
                    </p>
                    <p className="text-2xl font-bold text-green-800">
                      {stats.ultimoAnio}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="text-purple-600 text-2xl mr-3">🎯</div>
                  <div>
                    <p className="text-sm text-purple-600 font-medium">
                      Promedio Dificultad
                    </p>
                    <p className="text-2xl font-bold text-purple-800">
                      {stats.promedioDificultad.toFixed(1)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="text-orange-600 text-2xl mr-3">⏱️</div>
                  <div>
                    <p className="text-sm text-orange-600 font-medium">
                      Tiempo Promedio
                    </p>
                    <p className="text-2xl font-bold text-orange-800">
                      {stats.tiempoPromedio}s
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabla de Preguntas */}
            <PreguntasPAESTable
              preguntas={getCurrentPageData()}
              loading={loading}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              onView={handleView}
            />

            {/* Paginación */}
            {preguntas.length > 0 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  {(() => {
                    const { startItem, endItem, total } = getPaginationInfo();
                    return `Mostrando ${startItem} a ${endItem} de ${total} preguntas`;
                  })()}
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>

                  <div className="flex space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === page
                              ? "bg-blue-600 text-white"
                              : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case "simulaciones":
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Preguntas para Simulaciones
            </h2>
            <p className="text-gray-600 mb-6">
              Gestiona las preguntas disponibles para simulaciones PAES (solo
              estado SIMULACION, cualquier tipoSimulacion).
            </p>

            {/* Estadísticas rápidas */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="text-blue-600 text-2xl mr-3">📚</div>
                  <div>
                    <p className="text-sm text-blue-600 font-medium">
                      Total Preguntas
                    </p>
                    <p className="text-2xl font-bold text-blue-800">
                      {stats.totalPreguntas}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="text-green-600 text-2xl mr-3">📅</div>
                  <div>
                    <p className="text-sm text-green-600 font-medium">
                      Último Año
                    </p>
                    <p className="text-2xl font-bold text-green-800">
                      {stats.ultimoAnio}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="text-purple-600 text-2xl mr-3">🎯</div>
                  <div>
                    <p className="text-sm text-purple-600 font-medium">
                      Promedio Dificultad
                    </p>
                    <p className="text-2xl font-bold text-purple-800">
                      {stats.promedioDificultad.toFixed(1)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-orange-50 p-4 rounded-lg">
                <div className="flex items-center">
                  <div className="text-orange-600 text-2xl mr-3">⏱️</div>
                  <div>
                    <p className="text-sm text-orange-600 font-medium">
                      Tiempo Promedio
                    </p>
                    <p className="text-2xl font-bold text-orange-800">
                      {stats.tiempoPromedio}s
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabla de Preguntas */}
            <PreguntasPAESTable
              preguntas={getCurrentPageData()}
              loading={loading}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              onView={handleView}
            />

            {/* Paginación */}
            {preguntas.length > 0 && (
              <div className="mt-6 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  {(() => {
                    const { startItem, endItem, total } = getPaginationInfo();
                    return `Mostrando ${startItem} a ${endItem} de ${total} preguntas`;
                  })()}
                </div>

                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>

                  <div className="flex space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            currentPage === page
                              ? "bg-blue-600 text-white"
                              : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          {page}
                        </button>
                      )
                    )}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case "resultados":
        return (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Resultados de Simulaciones
            </h2>
            <p className="text-gray-600 mb-6">
              Visualiza y analiza los resultados de las simulaciones PAES.
            </p>

            <div className="bg-gray-50 rounded-lg p-6 text-center">
              <div className="text-gray-500 text-6xl mb-4">📈</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                Análisis de Resultados
              </h3>
              <p className="text-gray-500">
                Próximamente: Análisis detallado de resultados
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white">
          <h1 className="text-3xl font-bold mb-2">Simulaciones PAES</h1>
          <p className="text-blue-100">
            Gestiona preguntas, simulaciones y resultados de las pruebas PAES
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center">
                    <span className="mr-2">{tab.icon}</span>
                    {tab.name}
                  </div>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab content */}
          <div className="p-6">{renderTabContent()}</div>
        </div>
      </div>

      {/* Modal de Importación JSON */}
      <ImportarJSONModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSuccess={handleImportSuccess}
      />

      {/* Modal de Importación Excel */}
      <ImportarExcelModal
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        onImportSuccess={handleImportSuccess}
      />

      {/* Modal de Detalles de Pregunta */}
      <PreguntaDetailModal
        pregunta={selectedPregunta}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedPregunta(null);
        }}
        mode={modalMode}
        onSave={handleSavePregunta}
      />

      {/* Modal de Confirmación de Eliminación */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg
                    className="h-6 w-6 text-red-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Confirmar eliminación
                  </h3>
                </div>
              </div>

              <div className="mb-6">
                <p className="text-sm text-gray-500">
                  ¿Estás seguro de que quieres eliminar esta pregunta? Esta
                  acción no se puede deshacer.
                </p>
                {preguntaToDelete && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-md">
                    <p className="text-sm font-medium text-gray-900">
                      Pregunta #{preguntaToDelete.numero} -{" "}
                      {preguntaToDelete.anio}
                    </p>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {preguntaToDelete.contenido.substring(0, 100)}...
                    </p>
                  </div>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={handleDeleteCancel}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md font-medium transition-colors"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default SimulacionesPage;
